<template>
  <div class="config-panel">
    <div class="config-header">
      <h3>自动刷新配置</h3>
      <button class="close-btn" @click="$emit('close')">×</button>
    </div>
    
    <div class="config-content">
      <div class="config-item">
        <label>刷新间隔</label>
        <div class="slider-container">
          <input 
            type="range" 
            v-model="config.interval" 
            :min="config.minInterval" 
            :max="config.maxInterval"
            :step="config.step"
            @input="updateInterval"
            @keydown="handleSliderKeydown"
            ref="sliderInput"
            tabindex="0"
          >
          <div class="value-input-container">
            <input 
              type="number" 
              v-model.number="intervalValue" 
              :min="config.minInterval / 1000" 
              :max="config.maxInterval / 1000"
              @input="updateIntervalFromInput"
              @keydown.up.prevent="incrementValue"
              @keydown.down.prevent="decrementValue"
              class="value-input"
              ref="numberInput"
            >
            <span class="unit">{{ intervalUnit }}</span>
          </div>
        </div>
      </div>

      <div class="config-item">
        <label>日志级别</label>
        <select v-model="config.logLevel" @change="updateConfig">
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>
      </div>

      <div class="config-item">
        <label>
          <input type="checkbox" v-model="config.showTimestamp" @change="updateConfig">
          显示时间戳
        </label>
      </div>

      <div class="config-item">
        <label>
          <input type="checkbox" v-model="config.autoSave" @change="updateConfig">
          自动保存配置
        </label>
      </div>
    </div>

    <div class="config-footer">
      <button class="save-btn" @click="saveConfig">保存配置</button>
      <button class="reset-btn" @click="resetConfig">重置默认</button>
    </div>

    <!-- 保存成功提示 -->
    <div v-if="showSuccess" class="save-success">
      配置已保存
    </div>

    <!-- 保存确认对话框 -->
    <div v-if="showConfirm" class="confirm-dialog">
      <div class="confirm-content">
        <h4>配置已保存</h4>
        <p>是否立即生效？</p>
        <div class="confirm-buttons">
          <button class="confirm-btn" @click="applyNow">立即生效</button>
          <button class="cancel-btn" @click="closeConfig">稍后生效</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import { REFRESH_CONFIG, saveConfig as saveConfigToStorage, loadConfig } from '@/config/refresh'

export default {
  name: 'RefreshConfig',
  
  setup(props, { emit }) {
    const config = ref({ ...REFRESH_CONFIG })
    const showSuccess = ref(false)
    const showConfirm = ref(false)
    const intervalValue = ref(30) // 默认30秒
    const sliderInput = ref(null)
    const numberInput = ref(null)

    // 计算单位（秒或分钟）
    const intervalUnit = computed(() => {
      return '秒'
    })

    // 加载保存的配置
    onMounted(() => {
      const savedConfig = loadConfig()
      config.value = { ...config.value, ...savedConfig }
      // 初始化输入框的值
      intervalValue.value = config.value.interval / 1000
      // 设置滑块默认焦点
      nextTick(() => {
        sliderInput.value?.focus()
      })
    })

    // 处理滑块键盘事件
    const handleSliderKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        config.value.interval = Math.max(config.value.minInterval, config.value.interval - config.value.step)
        updateInterval()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        config.value.interval = Math.min(config.value.maxInterval, config.value.interval + config.value.step)
        updateInterval()
      }
    }

    // 增加数值
    const incrementValue = () => {
      const step = 5 // 5秒
      intervalValue.value = Math.min(
        config.value.maxInterval / 1000,
        intervalValue.value + step
      )
      updateIntervalFromInput()
    }

    // 减少数值
    const decrementValue = () => {
      const step = 5 // 5秒
      intervalValue.value = Math.max(
        config.value.minInterval / 1000,
        intervalValue.value - step
      )
      updateIntervalFromInput()
    }

    // 格式化时间间隔显示
    const formatInterval = (ms) => {
      return `${Math.round(ms / 1000)}秒`
    }

    // 从输入框更新间隔
    const updateIntervalFromInput = () => {
      // 确保值在范围内
      intervalValue.value = Math.max(
        config.value.minInterval / 1000,
        Math.min(config.value.maxInterval / 1000, intervalValue.value)
      )
      // 确保值是5的倍数
      intervalValue.value = Math.round(intervalValue.value / 5) * 5
      // 更新配置
      config.value.interval = intervalValue.value * 1000
      updateConfig()
    }

    // 从滑块更新间隔
    const updateInterval = () => {
      // 确保值是5的倍数
      config.value.interval = Math.round(config.value.interval / config.value.step) * config.value.step
      // 确保在范围内
      config.value.interval = Math.max(
        config.value.minInterval,
        Math.min(config.value.maxInterval, config.value.interval)
      )
      // 更新输入框的值
      intervalValue.value = config.value.interval / 1000
      updateConfig()
    }

    // 更新配置
    const updateConfig = () => {
      if (config.value.autoSave) {
        saveConfigToStorage(config.value)
      }
    }

    // 显示成功消息
    const showSuccessMessage = () => {
      showSuccess.value = true
      setTimeout(() => {
        showSuccess.value = false
      }, 1500)
    }

    // 保存配置
    const saveConfig = () => {
      saveConfigToStorage(config.value)
      showConfirm.value = true
    }

    // 立即生效
    const applyNow = () => {
      showConfirm.value = false
      showSuccess.value = true
      setTimeout(() => {
        showSuccess.value = false
        emit('close')
        // 触发页面刷新
        window.location.reload()
      }, 500)
    }

    // 稍后生效
    const closeConfig = () => {
      showConfirm.value = false
      showSuccess.value = true
      setTimeout(() => {
        showSuccess.value = false
        emit('close')
      }, 500)
    }

    // 重置配置
    const resetConfig = () => {
      config.value = { ...REFRESH_CONFIG }
      intervalValue.value = 30 // 重置为默认值
      saveConfigToStorage(config.value)
      showSuccessMessage()
    }

    return {
      config,
      showSuccess,
      showConfirm,
      intervalValue,
      intervalUnit,
      sliderInput,
      numberInput,
      formatInterval,
      updateConfig,
      updateInterval,
      updateIntervalFromInput,
      handleSliderKeydown,
      incrementValue,
      decrementValue,
      saveConfig,
      resetConfig,
      applyNow,
      closeConfig
    }
  }
}
</script>

<style scoped>
.config-panel {
  background-color: #2d2d2d;
  border-radius: 1rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #3a3a3a;
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.config-header h3 {
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: #4a4a4a;
  color: #fff;
}

.config-content {
  margin-bottom: 1.5rem;
}

.config-item {
  margin-bottom: 1.25rem;
}

.config-item label {
  display: block;
  margin-bottom: 0.5rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

input[type="range"] {
  flex: 1;
  height: 0.5rem;
  -webkit-appearance: none;
  background: transparent;
  border-radius: 0.25rem;
  outline: none;
  cursor: pointer;
  margin: 0.5rem 0;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 1.25rem;
  height: 1.25rem;
  background: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  margin-top: -0.5rem;
}

input[type="range"]::-webkit-slider-runnable-track {
  width: 100%;
  height: 0.5rem;
  background: #4a4a4a;
  border-radius: 0.25rem;
  position: relative;
}

input[type="range"]:focus::-webkit-slider-thumb {
  box-shadow: 0 0 0 2px #3b82f6;
  transform: scale(1.1);
}

input[type="range"]:focus::-webkit-slider-runnable-track {
  background: #5a5a5a;
}

/* Firefox 样式 */
input[type="range"]::-moz-range-thumb {
  width: 1.25rem;
  height: 1.25rem;
  background: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

input[type="range"]::-moz-range-track {
  width: 100%;
  height: 0.5rem;
  background: #4a4a4a;
  border-radius: 0.25rem;
}

input[type="range"]:focus::-moz-range-thumb {
  box-shadow: 0 0 0 2px #3b82f6;
  transform: scale(1.1);
}

input[type="range"]:focus::-moz-range-track {
  background: #5a5a5a;
}

.value-input-container {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 6rem;
}

.value-input {
  width: 4rem;
  padding: 0.25rem 0.5rem;
  background-color: #4a4a4a;
  border: 1px solid #5a5a5a;
  border-radius: 0.25rem;
  color: #fff;
  font-size: 0.875rem;
  text-align: right;
}

.value-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.unit {
  color: #9ca3af;
  font-size: 0.875rem;
  min-width: 2rem;
}

/* 移除数字输入框的上下箭头 */
.value-input::-webkit-outer-spin-button,
.value-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.value-input[type=number] {
  -moz-appearance: textfield;
}

/* 添加键盘焦点样式 */
input[type="range"]:focus {
  outline: none;
}

input[type="range"]:focus::-webkit-slider-thumb {
  box-shadow: 0 0 0 2px #3b82f6;
}

input[type="range"]:focus::-moz-range-thumb {
  box-shadow: 0 0 0 2px #3b82f6;
}

select {
  width: 100%;
  padding: 0.5rem;
  background-color: #4a4a4a;
  border: 1px solid #5a5a5a;
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.875rem;
  cursor: pointer;
}

select:focus {
  outline: none;
  border-color: #3b82f6;
}

input[type="checkbox"] {
  margin-right: 0.5rem;
  cursor: pointer;
}

.config-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.save-btn, .reset-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn {
  background-color: #3b82f6;
  color: #fff;
}

.save-btn:hover {
  background-color: #2563eb;
}

.reset-btn {
  background-color: #4a4a4a;
  color: #fff;
}

.reset-btn:hover {
  background-color: #5a5a5a;
}

.save-success {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: #10b981;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  animation: fadeInOut 1.5s ease-in-out;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translate(-50%, 1rem);
  }
  20% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
  80% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -1rem);
  }
}

@media (max-width: 768px) {
  .config-panel {
    max-width: 100%;
    border-radius: 0;
  }
}

/* 确认对话框样式 */
.confirm-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.confirm-content {
  background-color: #2d2d2d;
  border-radius: 1rem;
  padding: 1.5rem;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #3a3a3a;
  animation: slideIn 0.3s ease-out;
}

.confirm-content h4 {
  margin: 0 0 1rem 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
}

.confirm-content p {
  margin: 0 0 1.5rem 0;
  color: #9ca3af;
  font-size: 1rem;
}

.confirm-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.confirm-btn, .cancel-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-btn {
  background-color: #3b82f6;
  color: #fff;
}

.confirm-btn:hover {
  background-color: #2563eb;
}

.cancel-btn {
  background-color: #4a4a4a;
  color: #fff;
}

.cancel-btn:hover {
  background-color: #5a5a5a;
}

@keyframes slideIn {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style> 